import React, { useState, useEffect, Suspense } from 'react';
import { Layout, Menu, Watermark, message, Button, Typography, Spin, Drawer } from 'antd';
import {
  TrophyOutlined,
  CrownOutlined,
  HeartOutlined,
  CarOutlined,
  SearchOutlined,
  BarChartOutlined,
  SettingOutlined,
  CalculatorOutlined,
  QuestionCircleOutlined,
  FunctionOutlined,
  StarOutlined,
  ThunderboltOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { AppSettings } from './types';
import './App.css';
import './mobile.css';
import { announcementAPI, visitorAPI } from './services/api';
import { useResponsive } from './hooks/useResponsive';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { safeLocalStorage, safeGetUUID } from './utils/storage';
import { handleDebugTitleTap } from './utils/mobileDebug';

const NavigationGuide = lazyWithRetry(() => import('./components/NavigationGuide'), 'NavigationGuide');
const PowerRankings = lazyWithRetry(() => import('./components/PowerRankings'), 'PowerRankings');
const HeroRankings = lazyWithRetry(() => import('./components/HeroRankings'), 'HeroRankings');
const PetRankings = lazyWithRetry(() => import('./components/PetRankings'), 'PetRankings');
const RideRankings = lazyWithRetry(() => import('./components/RideRankings'), 'RideRankings');
const GodWarRankings = lazyWithRetry(() => import('./components/GodWarRankings'), 'GodWarRankings');
const ActivityRankings = lazyWithRetry(() => import('./components/ActivityRankings'), 'ActivityRankings');
const ExtraRankings = lazyWithRetry(() => import('./components/ExtraRankings'), 'ExtraRankings');
const LeagueRankings = lazyWithRetry(() => import('./components/LeagueRankings'), 'LeagueRankings');
const SearchPage = lazyWithRetry(() => import('./components/SearchPage'), 'SearchPage');
const DamageSimulator = lazyWithRetry(() => import('./components/DamageSimulator'), 'DamageSimulator');
const SystemPowerDetails = lazyWithRetry(() => import('./components/SystemPowerDetails'), 'SystemPowerDetails');
const StatisticsPage = lazyWithRetry(() => import('./components/StatisticsPage'), 'StatisticsPage');
const SettingsPanel = lazyWithRetry(() => import('./components/SettingsPanel'), 'SettingsPanel');
const MagicToolbox = lazyWithRetry(() => import('./components/magicTools'), 'MagicToolbox');
const RefinementSimulator = lazyWithRetry(() => import('./components/RefinementSimulator'), 'RefinementSimulator');

const { Header, Content, Sider } = Layout;
const { Text } = Typography;

const PAGE_PATHS: Record<string, string> = {
  guide: '/guide',
  power: '/power',
  hero: '/hero',
  pet: '/pet',
  ride: '/ride',
  godwar: '/godwar',
  tiandao: '/tiandao',
  teacher: '/teacher',
  achievement: '/achievement',
  activity: '/activity',
  league: '/league',
  search: '/search',
  calculator: '/calculator',
  systempower: '/systempower',
  statistics: '/statistics',
  settings: '/settings',
  refinement: '/refinement',
  magicTools: '/magic-tools',
};

const getPageFromLocation = () => {
  const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();
  const matchedPage = Object.keys(PAGE_PATHS).find(key => PAGE_PATHS[key] === path);
  if (matchedPage) return matchedPage;
  if (!path) {
    try {
      const saved = safeLocalStorage.getItem('ranking-dashboard-settings');
      const defaultPage = saved ? JSON.parse(saved)?.defaultPage : undefined;
      if (defaultPage && Object.prototype.hasOwnProperty.call(PAGE_PATHS, defaultPage)) return defaultPage;
    } catch {
      // 无法读取默认页面时使用战力榜。
    }
  }
  return 'power';
};

const App: React.FC = () => {
  const { isMobile } = useResponsive();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>(getPageFromLocation);
  const [settings, setSettings] = useState<AppSettings>({
    useUnits: false,
    pageSize: 50,
    serverId: null
  });
  const [announcement, setAnnouncement] = useState<string>('');
  const [visitors, setVisitors] = useState<{ online: number; total: number }>({ online: 0, total: 0 });

  // 保存设置到 localStorage
  useEffect(() => {
    try {
      const savedSettings = safeLocalStorage.getItem('ranking-dashboard-settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings) as AppSettings;
        setSettings(parsed);
      }
    } catch (error) {
      console.error('加载设置失败:', error);
    }
  }, []);
  useEffect(() => {
    const handlePopState = () => {
      setSelectedKey(getPageFromLocation());
      document.querySelector('.site-layout .ant-layout-content')?.scrollTo(0, 0);
      window.scrollTo(0, 0);
      setMobileMenuVisible(false);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  // 手机端默认收起侧边栏
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setCollapsed(true);
    }
  }, []);

  // 获取公告与访客的副作用
  useEffect(() => {
    announcementAPI.getAnnouncement()
      .then(data => setAnnouncement(data.announcement))
      .catch(() => message.error('加载公告失败'));

    // 获取访问统计，使用本地唯一访客ID
    let vid = safeLocalStorage.getItem('visitor_id');
    if (!vid) {
      vid = safeGetUUID();
      safeLocalStorage.setItem('visitor_id', vid);
    }
    const visitorId = vid || '';
    const controller = new AbortController();
    let pending = false;
    const updateVisitors = async () => {
      if (document.visibilityState !== 'visible' || pending || controller.signal.aborted) return;
      pending = true;
      try {
        const data = await visitorAPI.getVisitors(visitorId, controller.signal);
        if (!controller.signal.aborted) setVisitors({ online: data.online, total: data.total });
      } catch {
        if (!controller.signal.aborted) console.error('加载访问统计失败');
      } finally {
        pending = false;
      }
    };
    void updateVisitors();
    const interval = window.setInterval(() => void updateVisitors(), 60_000);
    document.addEventListener('visibilitychange', updateVisitors);
    return () => {
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', updateVisitors);
    };
  }, []);

  const saveSettings = (newSettings: AppSettings) => {
    setSettings(newSettings);
    safeLocalStorage.setItem('ranking-dashboard-settings', JSON.stringify(newSettings));
    message.success('设置已保存');
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    const path = PAGE_PATHS[key];
    if (!path) return;
    if (window.location.pathname !== path) window.history.pushState(null, '', path);
    setSelectedKey(key);
    document.querySelector('.site-layout .ant-layout-content')?.scrollTo(0, 0);
    window.scrollTo(0, 0);
    if (isMobile) {
      setMobileMenuVisible(false);
    }
  };

  // 取消弹窗公告，改为在“使用指南”页展示

  const menuItems = [
    {
      key: 'guide',
      icon: <QuestionCircleOutlined />,
      label: '使用指南',
    },
    {
      key: 'power',
      icon: <TrophyOutlined />,
      label: '战力排行榜',
    },
    {
      key: 'hero',
      icon: <CrownOutlined />,
      label: '角色排行榜',
    },
    {
      key: 'pet',
      icon: <HeartOutlined />,
      label: '宠物排行榜',
    },
    {
      key: 'ride',
      icon: <CarOutlined />,
      label: '坐骑排行榜',
    },
    {
      key: 'godwar',
      icon: <ThunderboltOutlined />,
      label: '神魔排行榜',
    },
    ...[
      { key: 'tiandao', label: '天道排行榜' },
      { key: 'teacher', label: '名师排行榜' },
      { key: 'achievement', label: '成就排行榜' },
    ].map(item => ({ ...item, icon: <StarOutlined /> })),
    {
      key: 'activity',
      icon: <StarOutlined />,
      label: '活动排行榜',
    },
    {
      key: 'league',
      icon: <CrownOutlined />,
      label: '联盟排行榜',
    },
    {
      key: 'search',
      icon: <SearchOutlined />,
      label: '全局搜索',
    },
    {
      key: 'calculator',
      icon: <CalculatorOutlined />,
      label: '六维计算器',
    },
    // {
    //   key: 'refinement',
    //   icon: <ToolOutlined />,
    //   label: '洗练模拟器',
    // },
    // {
    //   key: 'magicTools',
    //   icon: <ToolOutlined />,
    //   label: '妙妙工具',
    // },
    {
      key: 'systempower',
      icon: <FunctionOutlined />,
      label: '角色各系统详情',
    },
    {
      key: 'statistics',
      icon: <BarChartOutlined />,
      label: '统计分析',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
  ];

  const renderContent = () => {
    switch (selectedKey) {
      case 'guide':
        return <NavigationGuide settings={settings} announcement={announcement} onNavigate={(key) => handleMenuClick({ key })} />;
      case 'power':
        return <PowerRankings settings={settings} />;
      case 'hero':
        return <HeroRankings settings={settings} />;
      case 'pet':
        return <PetRankings settings={settings} />;
      case 'ride':
        return <RideRankings settings={settings} />;
      case 'godwar':
        return <GodWarRankings settings={settings} />;
      case 'activity':
        return <ActivityRankings settings={settings} />;
      case 'tiandao':
      case 'teacher':
      case 'achievement':
        return <ExtraRankings key={`${selectedKey}:${settings.serverId}:${settings.pageSize}`} kind={selectedKey} settings={settings} />;
      case 'league':
        return <LeagueRankings settings={settings} />;
      case 'search':
        return <SearchPage settings={settings} />;
      case 'calculator':
        return <DamageSimulator settings={settings} />;
      case 'refinement':
        return <RefinementSimulator />;
      case 'magicTools':
        return <MagicToolbox />;
      case 'systempower':
        return <SystemPowerDetails settings={settings} />;
      case 'statistics':
        return <StatisticsPage settings={settings} />;
      case 'settings':
        return <SettingsPanel settings={settings} onSave={saveSettings} />;
      default:
        return <PowerRankings settings={settings} />;
    }
  };

  return (
    <Watermark
      content="zmwsrank.top"
      gap={[150, 100]}
      className="app-watermark"
      font={{
        color: 'rgba(0, 0, 0, 0.14)',
        fontSize: 16
      }}
    >
      <Layout className="app-shell" style={{ minHeight: '100vh' }}>
        {/* 桌面端侧边栏 */}
        {!isMobile && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={(value: boolean) => setCollapsed(value)}
            trigger={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '100%' }}>
                {collapsed ? (
                  <MenuUnfoldOutlined style={{ fontSize: 16 }} />
                ) : (
                  <><MenuFoldOutlined style={{ fontSize: 16 }} /><span style={{ marginLeft: 4, fontSize: 14 }}>收起</span></>
                )}
              </div>
            }
            theme="light"
            style={{
              boxShadow: '2px 0 8px 0 rgba(29, 35, 41, 0.05)',
            }}
          >
            <div style={{
              height: 32,
              margin: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: collapsed ? '18px' : '16px',
              fontWeight: 'bold',
              color: '#1890ff'
            }}>
              {collapsed ? '榜' : '排行榜'}
            </div>
            <Menu
              theme="light"
              defaultSelectedKeys={['power']}
              selectedKeys={[selectedKey]}
              mode="inline"
              items={menuItems}
              onClick={handleMenuClick}
            />
          </Sider>
        )}

        {/* 移动端抽屉菜单 */}
        {isMobile && (
          <Drawer
            title={
              <div style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: 18,
                fontWeight: 'bold',
                color: '#1890ff'
              }}>
                排行榜导航
              </div>
            }
            placement="left"
            onClose={() => setMobileMenuVisible(false)}
            open={mobileMenuVisible}
            width="min(340px, 88vw)"
            className="mobile-navigation-drawer"
            styles={{
              body: { padding: 0 }
            }}
          >
            <Menu
              theme="light"
              selectedKeys={[selectedKey]}
              mode="inline"
              items={menuItems}
              onClick={handleMenuClick}
              style={{ border: 'none' }}
            />
          </Drawer>
        )}

        <Layout className="site-layout">
          <Header className="app-header" style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: '#fff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: isMobile ? 56 : 64,
          }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileMenuVisible(true)}
                aria-label="打开全部功能导航"
                aria-expanded={mobileMenuVisible}
                style={{ fontSize: 18, padding: '4px 8px' }}
              />
            )}
            <h1
              onClick={handleDebugTitleTap}
              title="连续点击5次可唤起移动端调试控制台"
              style={{
                margin: 0,
                color: '#1890ff',
                fontSize: isMobile ? 16 : 24,
                flex: 1,
                textAlign: isMobile ? 'center' : 'left',
                cursor: 'pointer',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              {isMobile ? menuItems.find(item => item.key === selectedKey)?.label || '排行榜' : '游戏排行榜数据'}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 8 }}>
              {!isMobile && (
                <Text style={{ marginRight: 8, color: '#666', fontSize: 14 }}>
                  在线: {visitors.online} 今日访问: {visitors.total}
                </Text>
              )}
              <Button
                type="text"
                icon={<QuestionCircleOutlined />}
                onClick={() => handleMenuClick({ key: 'guide' })}
                aria-label="查看使用指南"
                style={{ color: '#666', fontSize: isMobile ? 18 : 16 }}
                size={isMobile ? 'small' : 'middle'}
              />
            </div>
          </Header>

          <Content className={`page-content page-${selectedKey}`} style={{
            margin: isMobile ? '8px' : '16px',
            padding: isMobile ? '12px' : '24px',
            background: '#fff',
            borderRadius: isMobile ? 0 : 8,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            overflow: 'auto',
            overflowY: 'auto'
          }}>
            <Suspense
              fallback={(
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: isMobile ? '40px 0' : '80px 0' }}>
                  <Spin size="large" />
                  <Text type="secondary" style={{ marginTop: 16, fontSize: isMobile ? 14 : 16 }}>加载模块中...</Text>
                </div>
              )}
            >
              {renderContent()}
            </Suspense>
          </Content>
        </Layout>
      </Layout>
      {isMobile && (
        <nav className="mobile-tab-bar" aria-label="常用功能">
          {[
            { key: 'power', label: '排行榜', icon: <TrophyOutlined /> },
            { key: 'search', label: '搜索', icon: <SearchOutlined /> },
            { key: 'calculator', label: '计算器', icon: <CalculatorOutlined /> },
            { key: 'more', label: '全部功能', icon: <MenuOutlined /> },
          ].map(item => (
            <button
              key={item.key}
              type="button"
              aria-current={(selectedKey === item.key || (item.key === 'power' && ['hero', 'pet', 'ride', 'godwar', 'tiandao', 'teacher', 'achievement', 'activity', 'league'].includes(selectedKey))) ? 'page' : undefined}
              onClick={() => item.key === 'more' ? setMobileMenuVisible(true) : handleMenuClick({ key: item.key })}
            >
              {item.icon}<span>{item.label}</span>
            </button>
          ))}
        </nav>
      )}
    </Watermark>
  );
};

export default App;
